import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface Car {
  id: number;
  name: string;
  model: string;
  number: number;
  imei: string;
  isActive: boolean;
  airport?: string;
  createdAt: string;
  updatedAt?: string;
  calibrationTablesCount?: number;
  calibrationTables?: any[];
}

export interface CreateCarRequest {
  name: string;
  model: string;
  number: number;
  imei: string;
  airport?: string;
  isActive?: boolean;
}

export interface UpdateCarRequest {
  name?: string;
  model?: string;
  number?: number;
  imei?: string;
  airport?: string;
  isActive?: boolean;
}

export interface CarSearchRequest {
  name?: string;
  model?: string;
  number?: number;
  imei?: string;
  airport?: string;
  isActive?: boolean;
}

export interface CarStats {
  totalCars: number;
  activeCars: number;
  inactiveCars: number;
  carsByAirport: Record<string, number>;
  carsByModel: Record<string, number>;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class CarService {
  private baseUrl = '/api/cars';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех машин с пагинацией
  async getCars(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<Car>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение машины по ID
  async getCarById(id: number): Promise<Car> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение машины по IMEI
  async getCarByImei(imei: string): Promise<Car> {
    const url = getDataApiUrl(`${this.baseUrl}/imei/${encodeURIComponent(imei)}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Создание новой машины
  async createCar(carData: CreateCarRequest): Promise<Car> {
    const url = getDataApiUrl(this.baseUrl);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(carData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Обновление машины
  async updateCar(id: number, carData: UpdateCarRequest): Promise<Car> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(carData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Удаление машины
  async deleteCar(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Поиск машин
  async searchCars(searchData: CarSearchRequest, page = 0, size = 20): Promise<PaginatedResponse<Car>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение стандартных машин
  getDefaultCars(): CreateCarRequest[] {
    return [
      {
        name: "Топливозаправщик №1",
        model: "КАМАЗ-53215",
        number: 101,
        imei: "123456789012345",
        isActive: true,
        airport: "ASTANA"
      },
      {
        name: "Топливозаправщик №2",
        model: "КАМАЗ-53215",
        number: 102,
        imei: "123456789012346",
        isActive: true,
        airport: "ASTANA"
      },
      {
        name: "Топливозаправщик №3",
        model: "КАМАЗ-53215",
        number: 103,
        imei: "123456789012347",
        isActive: false,
        airport: "ALMATY"
      }
    ];
  }

  // Получение активных машин
  async getActiveCars(): Promise<Car[]> {
    const url = getDataApiUrl(`${this.baseUrl}/active`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение активных машин с пагинацией
  async getActiveCarsPaged(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<Car>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/active/paged?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение неактивных машин
  async getInactiveCars(): Promise<Car[]> {
    const url = getDataApiUrl(`${this.baseUrl}/inactive`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение неактивных машин с пагинацией
  async getInactiveCarsPaged(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<Car>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/inactive/paged?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Переключение активности машины
  async toggleCarActive(id: number): Promise<Car> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}/toggle`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение статистики машин
  async getCarStats(): Promise<CarStats> {
    const url = getDataApiUrl(`${this.baseUrl}/stats`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }
}

const carService = new CarService();
export default carService; 